import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useAuthState } from '@percent/partner-dashboard/common/hooks'

/**
 * Clears the React Query cache whenever the authenticated identity changes, so a
 * new partner never sees the previous partner's cached data after a logout→login
 * in the same SPA session (NEX-763). Query keys aren't partner-scoped, so without
 * this a fresh login reads the prior account's cached entries until each query
 * refetches. Renders `null`; must sit inside both AuthContextController (for the
 * auth state) and QueryClientProvider (for the cache).
 */
export function QueryCacheAuthReset() {
  const queryClient = useQueryClient()
  const { user } = useAuthState()

  // Account id + partner id together identify "who is logged in"; empty on logout.
  const identity = `${user?.id ?? ''}:${user?.partnerId ?? ''}`
  const previousIdentity = useRef(identity)

  useEffect(() => {
    if (previousIdentity.current === identity) return

    previousIdentity.current = identity
    // A different account (or logout) is now active — drop the prior account's
    // cached queries so nothing bleeds across the boundary.
    queryClient.clear()
  }, [identity, queryClient])

  return null
}
